/**
 * Dutch Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const nl: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.funds': 'Fondsen',
  'nav.investments': 'Investeringen',
  'nav.investees': 'Deelnemingen',
  'nav.esg': 'ESG Beoordeling',
  'nav.compliance': 'Naleving',
  'nav.reports': 'Rapporten',
  'nav.settings': 'Instellingen',
  'nav.users': 'Gebruikers',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicatoren',
  'nav.taxonomy': 'EU-taxonomie',
  'nav.filings': 'Rapportages',
  'nav.calendar': 'Kalender',
  'nav.submit': 'Gegevens indienen',
  'nav.submissions': 'Mijn inzendingen',
  'nav.benchmarks': 'Benchmarks',
  'nav.messages': 'Berichten',
  'nav.portfolio': 'Portefeuille',
  'nav.performance': 'Fondsprestaties',
  'nav.esgSummary': 'ESG Samenvatting',
  'nav.pipeline': 'Pijplijn',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Impactmetingen',
  'nav.sdg': 'SDG-mapping',
  'nav.audit': 'Auditspoor',
  'nav.snapshots': 'Datamomentopnamen',
  'nav.knowledgeGraph': 'Kennisgraaf',
  'nav.geographic': 'Geografisch',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Assistent',
  'nav.tasks': 'Taken',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regelgevingsmonitor',

  // Common Actions
  'action.create': 'Aanmaken',
  'action.edit': 'Bewerken',
  'action.delete': 'Verwijderen',
  'action.save': 'Opslaan',
  'action.cancel': 'Annuleren',
  'action.submit': 'Indienen',
  'action.approve': 'Goedkeuren',
  'action.reject': 'Afwijzen',
  'action.download': 'Downloaden',
  'action.export': 'Exporteren',
  'action.import': 'Importeren',
  'action.filter': 'Filteren',
  'action.search': 'Zoeken',
  'action.refresh': 'Vernieuwen',
  'action.close': 'Sluiten',
  'action.back': 'Terug',
  'action.next': 'Volgende',
  'action.previous': 'Vorige',
  'action.viewAll': 'Alles bekijken',
  'action.viewDetails': 'Details bekijken',
  'action.generate': 'Genereren',
  'action.signOut': 'Uitloggen',
  'action.addNew': 'Nieuwe toevoegen',
  'action.invite': 'Uitnodigen',

  // Statuses
  'status.draft': 'Concept',
  'status.submitted': 'Ingediend',
  'status.validated': 'Gevalideerd',
  'status.approved': 'Goedgekeurd',
  'status.rejected': 'Afgewezen',
  'status.pending': 'In afwachting',
  'status.inProgress': 'In uitvoering',
  'status.overdue': 'Achterstallig',
  'status.compliant': 'Conform',
  'status.filed': 'Ingediend',
  'status.active': 'Actief',
  'status.inactive': 'Inactief',
  'status.closed': 'Gesloten',
  'status.fundraising': 'Fondsenwerving',
  'status.investing': 'Investeren',
  'status.harvesting': 'Oogst',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portefeuilleoverzicht',
  'dashboard.complianceStatus': 'Nalevingsstatus',
  'dashboard.collectionProgress': 'Verzamelingsvoortgang',
  'dashboard.activityFeed': 'Activiteit',
  'dashboard.quickActions': 'Snelle acties',
  'dashboard.investmentPipeline': 'Investeringspijplijn',
  'dashboard.totalFunds': 'Totaal fondsen',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Actieve investeringen',
  'dashboard.totalInvestees': 'Deelnemingen',
  'dashboard.recentActivity': 'Recente activiteit',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Totaal gecommitteerd',
  'lp.capitalCalled': 'Opgevraagd kapitaal',
  'lp.distributed': 'Gedistribueerd',
  'lp.netPosition': 'Nettopositie',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondsprestaties',
  'lp.esgSummary': 'ESG Samenvatting',
  'lp.reportLibrary': 'Rapportbibliotheek',
  'lp.taxonomyAligned': 'Taxonomie-afgestemd',
  'lp.paiCoverage': 'PAI Dekking',
  'lp.portfolioPerformance': 'LP Portefeuilleprestaties',

  // Fund Labels
  'fund.name': 'Fondsnaam',
  'fund.code': 'Fondscode',
  'fund.vintage': 'Oprichtingsjaar',
  'fund.size': 'Fondsomvang',
  'fund.targetSize': 'Doelomvang',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategie',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Categorie',
  'fund.geographyFocus': 'Geografische focus',
  'fund.sectorFocus': 'Sectorfocus',

  // Form Labels
  'form.name': 'Naam',
  'form.email': 'E-mail',
  'form.country': 'Land',
  'form.amount': 'Bedrag',
  'form.currency': 'Valuta',
  'form.date': 'Datum',
  'form.description': 'Beschrijving',
  'form.notes': 'Opmerkingen',
  'form.type': 'Type',
  'form.period': 'Periode',
  'form.year': 'Jaar',
  'form.required': 'Verplicht',
  'form.optional': 'Optioneel',

  // Table Headers
  'table.fund': 'Fonds',
  'table.commitment': 'Commitment',
  'table.called': 'Opgevraagd',
  'table.distributed': 'Gedistribueerd',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regelgeving',
  'table.filingType': 'Rapportagetype',
  'table.reportingPeriod': 'Periode',
  'table.createdAt': 'Aangemaakt',
  'table.actions': 'Acties',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU-taxonomie',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Datavolledigheid',
  'compliance.nextDeadline': 'Volgende deadline',

  // Reports
  'report.quarterly': 'Kwartaalrapport',
  'report.annual': 'Jaarverslag',
  'report.paiStatement': 'PAI Verklaring',
  'report.taxonomyReport': 'Taxonomierapport',
  'report.generatedAt': 'Gegenereerd',
  'report.version': 'Versie',

  // General
  'general.loading': 'Laden...',
  'general.noData': 'Geen gegevens beschikbaar',
  'general.error': 'Er is een fout opgetreden',
  'general.retry': 'Opnieuw proberen',
  'general.total': 'Totaal',
  'general.page': 'Pagina',
  'general.of': 'van',
  'general.showing': 'Weergave',
  'general.results': 'resultaten',
  'general.language': 'Taal',
  'general.english': 'Engels',
  'general.french': 'Frans',
};
